import Image from 'next/image';
import dayjs from 'dayjs';
import { Key, useState } from 'react';
import { useRouter } from 'next/router';
import { sourceIcon } from '../../utils/sourceIcon';
import { useRef, useEffect } from 'react';
import { Rating } from 'react-simple-star-rating';
import DetailedReviewModal from '../testimonials/detailedReviewModal';
import ImagesReviewModal from '../testimonials/imagesReviewModal';
import Link from 'next/link';
import { getDisplayName } from '../../utils/getDisplayName';

interface IProps {
  review: Record<string, any>;
  rowImageCount?: number;
}

const ReviewCard = ({ review, rowImageCount }: IProps) => {
  const router = useRouter();
  const isClient = typeof window !== 'undefined';
  const isToursPath = isClient && router.asPath.includes('/tours/');
  const [showModal, setShowModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [randomName] = useState(() => {
    const names = [
      'Alice',
      'Bob',
      'Charlie',
      'David',
      'Eva',
      'Frank',
      'Grace',
      'Henry',
      'Ivy',
      'Jack',
      'Kate',
      'Leo'
    ];
    return names[Math.floor(Math.random() * names?.length)];
  });
  const openModalAtIndex = (index: number) => {
    setModalImageIndex(index);
    setShowModal(true);
  };

  const reviewText = review?.review || '';
  const safeRowImageCount = rowImageCount ?? 0;

  const avatarBgColor = useState(() => {
    const colors = [
      '#B9BAFC',
      '#E4E46E',
      '#CFE0C6',
      '#FFC0CB',
      '#FAD797',
      '#E7CFD1'
    ];
    return colors[Math.floor(Math.random() * colors?.length)];
  })[0];

  const initial = (
    review?.traveller?.charAt(0) ||
    review?.name?.charAt(0) ||
    randomName?.charAt(0)
  )?.toUpperCase();
  const isVideoUrl = (url: string): boolean => {
    const videoExtensions = [
      'mp4',
      'mov',
      'avi',
      'wmv',
      'flv',
      'mkv',
      'webm',
      'ogg'
    ];
    const extension = url?.split('.').pop()?.toLowerCase() || '';
    return videoExtensions?.includes(extension);
  };
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const [visibleLines, setVisibleLines] = useState(0);
  const [totalLines, setTotalLines] = useState(0);
  const hasImages =
    Array.isArray(review?.image_urls) && review?.image_urls?.length > 0;
  const imageCount = review?.image_urls?.length || 0;

  useEffect(() => {
    const el = textRef.current;

    const checkOverflow = () => {
      if (el) {
        const style = window.getComputedStyle(el);
        const lineHeight = parseFloat(style.lineHeight);
        const visibleHeight = el.clientHeight;
        const scrollHeight = el.scrollHeight;

        const visible = Math.round(visibleHeight / lineHeight);
        const total = Math.round(scrollHeight / lineHeight);
        if ((imageCount > 2 && visible > 5) || safeRowImageCount === 0) {
          setVisibleLines(5);
        } else if (review?.image_urls?.length < 3 && safeRowImageCount < 3) {
          setVisibleLines(visible > 12 ? 12 : visible);
        } else {
          setVisibleLines(visible > 18 ? 18 : visible);
        }

        setTotalLines(total);
      }
    };

    const timeout = setTimeout(checkOverflow, 50);
    return () => clearTimeout(timeout);
  }, [reviewText, imageCount]);

  const shouldShowReadMore = visibleLines > 0 && totalLines > visibleLines;
  return (
    <div
      key={review?.id}
      id="review-card"
      className={`flex flex-col justify-between  ${
        isToursPath
          ? 'w-full md:w-[49%] min-h-[250px] lg:w-[275px] xl:w-[48%]'
          : 'w-[288px] xl:w-[321px] min-h-[332px] xmsm:w-[304px] xsm:w-[340px]'
      } px-5 py-7 bg-[#f7f8fb] rounded-[1rem] relative`}
    >
      {/* Top Section */}
      <div className="flex w-full items-start justify-between">
        <div className="flex items-start gap-2">
          <div
            className="rounded-full w-[40px] h-[40px] mt-1 shrink-0 flex items-center justify-center text-white font-bold text-[18px]"
            style={{ backgroundColor: avatarBgColor }}
          >
            {initial}
          </div>
          <div className="flex flex-col justify-between">
            <div className="text-primary text-[20px] font-semibold w-[150px] break-words">
              {getDisplayName(review?.traveller || review?.name)}
            </div>

            <div className="text-primary text-[12px] font-normal">
              {dayjs(review?.created_at || new Date()).format('MMMM DD, YYYY')}
            </div>
          </div>
        </div>
        <div className="w-[48px] h-[48px] mt-1 shrink-0">
          <Image
            src={sourceIcon(review?.source)}
            alt="reviews"
            width={50}
            height={50}
            className="object-contain w-full h-full"
          />
        </div>
      </div>

      {/* Review Text */}
      <div className="flex flex-col flex-grow mt-2">
        <div className="w-full flex items-center mb-1">
          <p className="text-[15px] text-primary font-[700] mt-[3px] mr-2">{`${review?.rating}/5`}</p>
          <Rating
            initialValue={review?.rating}
            readonly
            iconsCount={5}
            size={16}
            allowHover={false}
            disableFillHover
            fillColor="orange"
            emptyColor="#9B9B9B"
            allowFraction
            SVGstyle={{
              display: 'inline-block'
            }}
          />
        </div>
        <p
          ref={textRef}
          className={`text-[15px] text-primary font-normal my-1 leading-[1.5] break-words overflow-hidden max-h-[${
            Math.min(visibleLines, 18) * 1.45
          }em]`}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: visibleLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {reviewText}
        </p>

        {shouldShowReadMore && (
          <div
            className="text-[#0077FF] text-[15px] underline cursor-pointer mb-3"
            onClick={() => setShowModal(true)}
          >
            Read more
          </div>
        )}
        {review?.image_urls && (
          <div className="w-full flex flex-wrap justify-between">
            {review?.image_urls
              ?.slice(0, 4)
              ?.map((url: string, index: number) => (
                <div
                  key={index}
                  className="w-[49%] aspect-[1/1] relative mb-2 cursor-pointer"
                  onClick={() => openModalAtIndex(index)}
                >
                  {' '}
                  {isVideoUrl(url) ? (
                    <div className="relative w-full h-full">
                      <video
                        src={url}
                        className="object-cover w-full h-full rounded-[0.5rem]"
                        controls={false}
                        muted
                        playsInline
                        loop
                        preload="metadata"
                      >
                        {url}
                      </video>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-[0.5rem]">
                        <div className="bg-white/80 rounded-full p-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-black"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Image
                      src={url}
                      alt={`${getDisplayName(
                        review?.traveller || review?.name
                      )} enjoying ${
                        review?.parentproduct?.name || 'tour'
                      } ${dayjs(review?.created_at || new Date()).format(
                        'YYYY-MM-DD'
                      )}${
                        review?.image_urls?.length > 1 ? ` ${index + 1}` : ''
                      }`}
                      width={150}
                      height={150}
                      className="object-cover w-full h-full rounded-[0.5rem]"
                    />
                  )}
                  {index === 3 && review?.image_urls?.length > 4 && (
                    <div
                      onClick={e => {
                        e.stopPropagation();
                        openModalAtIndex(0);
                      }}
                      className="absolute top-0 left-0 w-full h-full bg-black/60 flex items-center justify-center text-white text-xl font-semibold rounded-[0.5rem] cursor-pointer"
                    >
                      +{review?.image_urls?.length - 4}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>

      {!isToursPath && (
        <div className="w-full flex justify-between items-center">
          <Link
            href={`/tours/${review?.parentproduct?.slug}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="text-[14px] font-[400] underline text-primary cursor-pointer">
              {review?.parentproduct?.name?.length > 24
                ? `${review?.parentproduct?.name?.slice(0, 24)}...`
                : review?.parentproduct?.name}
            </div>
          </Link>
          <div className="w-[84px] min-w-[80px] h-[24px] bg-[#dbf0ec] text-[14px] font-[400] text-[#10B981] rounded-[1.2rem] flex items-center justify-center gap-1">
            <Image
              src="/verified-tick-green.svg"
              width={16}
              height={16}
              alt="rating star"
            />
            verified
          </div>
        </div>
      )}

      {/* Modal for Detailed Review */}
      {showModal && hasImages && (
        <ImagesReviewModal
          setShowModal={setShowModal}
          productName={review?.parentproduct?.name}
          data={{
            traveller: review?.traveller,
            name: review?.name,
            author: review?.author,
            author_name: review?.author_name,
            rating: review?.rating,
            review: review?.review,
            text: review?.text,
            created_at: review?.created_at,
            published_at: review?.published_at,
            time: review?.time,
            author_image: review?.author_image,
            image: review?.image,
            profile_photo_url: review?.profile_photo_url,
            isVideo: review?.image_urls?.map((img: { url: string }) =>
              isVideoUrl(img?.url)
            )
          }}
          media={review?.image_urls}
          randomName={randomName}
          initialIndex={modalImageIndex}
        />
      )}
      {showModal && !hasImages && (
        <DetailedReviewModal setShowModal={setShowModal} data={review} />
      )}
    </div>
  );
};

export default ReviewCard;
